import { useCallback, useMemo } from 'react';
import usePersonalizationStrategy from 'personalization-lib/hooks/variants/usePersonalizationStrategy';
import useFetchAssetsByIds from 'personalization-lib/hooks/assets/useFetchAssetsByIds';
import { getPersonalizationAccess } from 'personalization-lib/utils/auth';
import { getAssetIds, getAllStrategyAssets } from 'personalization-lib/utils/personalizationStrategy/assets';
/**
 * Returns the variants for a given strategy along with asset metadata.
 *
 * @param strategyId - The id of the strategy to fetch variants for.
 * @returns An object containing:
 *   - variants: Array of objects with assetCoordinates, targetingConfig, and assetData
 *   - loading: Boolean indicating if data is being fetched
 *   - error: Any error that occurred during fetching
 *   - refetch: Function to manually refetch the data
 */
const useVariants = (strategyId, onError) => {
  const personalizationAccess = getPersonalizationAccess();
  const {
    strategy,
    loading: variantsLoading,
    error: variantsError,
    refetch: refetchVariants
  } = usePersonalizationStrategy(strategyId, {
    onError
  });
  const variants = useMemo(() => strategy ? getAllStrategyAssets(strategy) : [], [strategy]);
  const assetSource = strategy === null || strategy === void 0 ? void 0 : strategy.assetCoordinates.source;
  const assetIds = useMemo(() => getAssetIds(variants), [variants]);
  const {
    assetsById,
    loading: assetsLoading,
    error: assetsError,
    refetch: refetchAssets
  } = useFetchAssetsByIds(assetSource, assetIds, onError);
  const loading = variantsLoading || assetsLoading;
  const error = variantsError || assetsError;
  const refetch = useCallback(() => {
    if (!personalizationAccess.view) {
      return;
    }
    refetchVariants().then(({
      data: {
        result
      }
    }) => {
      const newVariants = getAllStrategyAssets(result.personalizationStrategy);
      if (getAssetIds(newVariants).length === assetIds.length) {
        // If the asset ids haven't changed, we need to manually trigger the assets refetch
        return refetchAssets();
      }
    }).catch(onError);
  }, [assetIds.length, refetchAssets, refetchVariants, onError, personalizationAccess.view]);
  return useMemo(() => ({
    variants: variants.map(variant => Object.assign({}, variant, {
      assetData: assetsById === null || assetsById === void 0 ? void 0 : assetsById[variant.assetCoordinates.id]
    })),
    refetch,
    loading,
    error
  }), [variants, assetsById, loading, error, refetch]);
};
export default useVariants;